import { useState } from 'react'
import Card from '../../../components/common/Card/Card'
import Badge from '../../../components/common/Badge/Badge'
import Button from '../../../components/common/Button/Button'
import {
  formatDate,
  formatHours,
  formatTimestamp,
  getStatusBadgeVariant
} from '../../../services/timesheetService'

/**
 * TimesheetCard Component
 * Display individual timesheet entry with status and actions
 *
 * @param {object} entry - Timesheet entry data
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {boolean} isOwnEntry - Whether this is the current user's entry
 * @param {function} onApprove - Approve callback (admin only)
 * @param {function} onReject - Reject callback (admin only)
 * @param {function} onEdit - Edit callback (employee only)
 * @param {function} onCancel - Cancel callback (employee only)
 */
const TimesheetCard = ({
  entry,
  isAdmin = false,
  isOwnEntry = false,
  onApprove,
  onReject,
  onEdit,
  onCancel
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false)

  const isPending = entry.status === 'pending'
  const isApproved = entry.status === 'approved'
  const isRejected = entry.status === 'rejected'
  const isCancelled = entry.status === 'cancelled'

  // Determine if actions should be shown
  const showAdminActions = isAdmin && isPending && !isOwnEntry
  const showEmployeeActions = isOwnEntry && isPending

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription)
  }

  const handleApprove = () => {
    if (window.confirm('Approve this timesheet entry?')) {
      onApprove(entry.id)
    }
  }

  const handleReject = () => {
    onReject(entry)
  }

  const handleEdit = () => {
    onEdit(entry)
  }

  const handleCancel = () => {
    if (window.confirm('Cancel this timesheet entry?')) {
      onCancel(entry.id)
    }
  }

  return (
    <Card style={{
      border: isPending ? '2px solid var(--psyber-warning)' : undefined,
      background: isPending ? 'rgba(255, 193, 7, 0.03)' : undefined
    }}>
      {/* Header: Employee Info (Admin View) */}
      {isAdmin && !isOwnEntry && (
        <div style={{
          marginBottom: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid var(--psyber-card-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <i className="fas fa-user" style={{ color: 'var(--psyber-text-secondary)' }}></i>
            <strong style={{ fontSize: 'var(--psyber-font-size-base)', color: 'var(--psyber-text)' }}>
              {entry.employeeName}
            </strong>
          </div>
          <div style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)', marginLeft: '1.5rem' }}>
            ID: {entry.employeeId}
          </div>
        </div>
      )}

      {/* Project and Status Badges */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1rem'
      }}>
        <Badge variant="info" icon="fas fa-project-diagram">
          {entry.projectName}
        </Badge>
        <Badge variant={getStatusBadgeVariant(entry.status)}>
          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
        </Badge>
      </div>

      {/* Date and Hours */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: 'var(--psyber-font-size-base)',
          color: 'var(--psyber-text)',
          marginBottom: '0.5rem'
        }}>
          <i className="fas fa-calendar-day" style={{ color: 'var(--psyber-primary)' }}></i>
          <strong>{formatDate(entry.date)}</strong>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: 'var(--psyber-font-size-base)',
          color: 'var(--psyber-text)'
        }}>
          <i className="fas fa-clock" style={{ color: 'var(--psyber-accent)' }}></i>
          <strong>{formatHours(entry.hours)}</strong>
        </div>
      </div>

      {/* Task Description */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          fontSize: 'var(--psyber-font-size-sm)',
          fontWeight: 600,
          color: 'var(--psyber-text-secondary)',
          marginBottom: '0.25rem'
        }}>
          Task Description:
        </div>
        <div style={{
          fontSize: 'var(--psyber-font-size-sm)',
          color: 'var(--psyber-text)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap'
        }}>
          {showFullDescription || entry.taskDescription.length <= 150
            ? entry.taskDescription
            : `${entry.taskDescription.substring(0, 150)}...`}
        </div>
        {entry.taskDescription.length > 150 && (
          <button
            onClick={toggleDescription}
            style={{
              marginTop: '0.25rem',
              background: 'none',
              border: 'none',
              color: 'var(--psyber-primary)',
              fontSize: 'var(--psyber-font-size-sm)',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline'
            }}
          >
            {showFullDescription ? 'Show Less' : 'Read More'}
          </button>
        )}
      </div>

      {/* Rejection Comments (if rejected) */}
      {isRejected && entry.reviewComments && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--psyber-error-bg)',
          border: '1px solid var(--psyber-error)',
          borderRadius: 'var(--psyber-radius-sm)'
        }}>
          <div style={{
            fontSize: 'var(--psyber-font-size-sm)',
            fontWeight: 600,
            color: 'var(--psyber-error)',
            marginBottom: '0.25rem'
          }}>
            Rejection Reason:
          </div>
          <div style={{
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text)',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap'
          }}>
            {entry.reviewComments}
          </div>
        </div>
      )}

      {/* Metadata Footer */}
      <div style={{
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--psyber-card-border)',
        fontSize: 'var(--psyber-font-size-xs)',
        color: 'var(--psyber-text-muted)'
      }}>
        <div style={{ marginBottom: '0.25rem' }}>
          <i className="fas fa-clock"></i> Submitted: {formatTimestamp(entry.createdAt)}
        </div>
        {(isApproved || isRejected) && entry.reviewedAt && (
          <div>
            <i className="fas fa-user-check"></i> Reviewed by {entry.reviewedByName} on {formatTimestamp(entry.reviewedAt)}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {(showAdminActions || showEmployeeActions) && (
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--psyber-card-border)',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap'
        }}>
          {/* Admin Actions */}
          {showAdminActions && (
            <>
              <Button
                variant="primary"
                icon="fas fa-check"
                onClick={handleApprove}
                style={{ flex: 1 }}
              >
                Approve
              </Button>
              <Button
                variant="ghost"
                icon="fas fa-times"
                onClick={handleReject}
                style={{ flex: 1 }}
              >
                Reject
              </Button>
            </>
          )}

          {/* Employee Actions */}
          {showEmployeeActions && (
            <>
              <Button
                variant="primary"
                icon="fas fa-edit"
                onClick={handleEdit}
                style={{ flex: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                icon="fas fa-ban"
                onClick={handleCancel}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

export default TimesheetCard
